// Define getConnectionString function first to avoid hoisting issues
const getConnectionString = () => {
    switch (process.env.REACT_APP_ENV) {
        case "dev": {
            return 'InstrumentationKey=6e10ba80-dd67-435f-bbf1-7f22c5caa245;IngestionEndpoint=https://centralus-2.in.applicationinsights.azure.com/;LiveEndpoint=https://centralus.livediagnostics.monitor.azure.com/;ApplicationId=64971909-18bd-448c-83fd-ab6d9da57d4d';
        }
        case "demo": {
            return 'InstrumentationKey=aa94041b-1cbb-4661-b056-d2923f8888f2;IngestionEndpoint=https://centralus-2.in.applicationinsights.azure.com/;LiveEndpoint=https://centralus.livediagnostics.monitor.azure.com';
        }
        case "prod": {
            return 'InstrumentationKey=70e83872-e658-4739-b1df-620726002bfa;IngestionEndpoint=https://centralus-2.in.applicationinsights.azure.com/;LiveEndpoint=https://centralus.livediagnostics.monitor.azure.com/;ApplicationId=e7c4d596-6555-4ec8-ae16-db6821d2ca90';
        }
        case "local": {
            return 'InstrumentationKey=6e10ba80-dd67-435f-bbf1-7f22c5caa245;IngestionEndpoint=https://centralus-2.in.applicationinsights.azure.com/;LiveEndpoint=https://centralus.livediagnostics.monitor.azure.com/;ApplicationId=64971909-18bd-448c-83fd-ab6d9da57d4d';
        }
        default:
            break;
    }
};

// Check if current route is a PDF route to completely skip Application Insights
const currentPath = window.location.pathname;
const isPdfRoute = currentPath.includes('/pdf/') ||
    currentPath.includes('/html/') ||
    currentPath.includes('/disconnect') ||
    currentPath.includes('/PrivacyPolicy') ||
    currentPath.includes('/TermsOfService');

let appInsights = null;
let reactPlugin = null;

if (isPdfRoute) {
    // For PDF routes, export null/dummy objects to completely prevent Application Insights
   // console.log('PDF route detected, disabling Application Insights');

    reactPlugin = null;
    appInsights = null;
} else {
    // Only import and initialize Application Insights for non-PDF routes
    try {
        const { ApplicationInsights } = require('@microsoft/applicationinsights-web');
        const { ReactPlugin } = require('@microsoft/applicationinsights-react-js');
        const { createBrowserHistory } = require('history');

        const browserHistory = createBrowserHistory({ basename: '' });
        reactPlugin = new ReactPlugin();

        const connectionString = getConnectionString();
        
        // Only initialize if we have a valid connection string
        if (connectionString) {
            appInsights = new ApplicationInsights({
                config: {
                    connectionString: connectionString,
                    disableAjaxTracking: false,
                    disableFetchTracking: false,
                    enableAutoRouteTracking: true,
                    enableUnhandledPromiseRejectionTracking: true,
                    // Add more specific error filtering
                    disableExceptionTracking: false,
                    // Enable source map support
                    enableDebug: false,
                    loggingLevelConsole: 0,
                    extensions: [reactPlugin],
                    extensionConfig: {
                        [reactPlugin.identifier]: { history: browserHistory }
                    }
                }
            });
        } else {
            if (process.env.NODE_ENV === 'development') {
                console.warn('AppInsights: No connection string available for current environment');
            }
            appInsights = null;
            reactPlugin = null;
        }
    } catch (error) {
        console.warn('Error initializing Application Insights:', error);
        reactPlugin = null;
        appInsights = null;
    }
}

// getConnectionString is now defined above

// Only set up telemetry initializers if Application Insights is actually created
if (!isPdfRoute && appInsights) {
    // Add telemetry initializer to filter out PostHog requests and PDF-related requests
    appInsights.addTelemetryInitializer((envelope) => {
        if (envelope.baseType === 'RemoteDependencyData') {
            const data = envelope.baseData;

            // Filter out PostHog requests
            if (data.target && data.target.includes('posthog.com')) {
                return false; // Don't send this telemetry
            }
            if (data.data && data.data.includes('posthog.com')) {
                return false; // Don't send this telemetry
            }

            // Filter out PDF-related requests that might cause issues
            if (data.data && (data.data.includes('blob:') || data.data.includes('fonts.googleapis.com') || data.data.includes('fonts.gstatic.com'))) {
                return false; // Don't send this telemetry
            }
        }

        return true; // Send all other telemetry
    });

    // Filter client-side noise: canceled requests, network errors, and browser permission/security errors
    appInsights.addTelemetryInitializer((envelope) => {
        if (envelope.baseType === 'ExceptionData') {
            const data = envelope.baseData;
            const exceptionType = data.exceptions && data.exceptions[0] && data.exceptions[0].typeName;
            const message = (data.exceptions && data.exceptions[0] && data.exceptions[0].message) || '';

            // Axios canceled requests (component unmount, debounce, etc.) - expected behavior
            if (exceptionType === 'CanceledError' || message.includes('CanceledError') || message.includes('canceled')) {
                return false;
            }

            // Axios network errors (offline, CORS, DNS) - not actionable server-side
            if (message.includes('Network Error') || message.includes('AxiosError: Network Error')) {
                return false;
            }

            // Browser geolocation permission denied
            if (message.includes('Location permission denied') || message.includes('User denied Geolocation')) {
                return false;
            }

            // Generic browser ErrorEvent wrappers with no useful stack
            if (exceptionType === 'ErrorEvent') {
                return false;
            }

            // Cross-origin security errors (blocked by browser same-origin policy)
            if (exceptionType === 'SecurityError' && message.includes('cross-origin')) {
                return false;
            }
        }

        return true;
    });

    // Add telemetry initializer to include user context in all telemetry
    appInsights.addTelemetryInitializer((envelope) => {
        try {
            // Import here to avoid circular dependency
            const localStorage = require('./utils/localStorage').default;
            const user = localStorage.getFullInfo();
            const companyId = localStorage.getCompanyId();

            if (user && user.nameid) {
                // Add user context to all telemetry
                envelope.tags = envelope.tags || {};
                envelope.tags['ai.user.id'] = user.nameid;

                // Add custom properties
                envelope.baseData = envelope.baseData || {};
                envelope.baseData.properties = envelope.baseData.properties || {};
                envelope.baseData.properties.userId = user.nameid;
                envelope.baseData.properties.userDisplayName = user.displayname || 'Unknown';

                if (companyId) {
                    envelope.baseData.properties.companyId = companyId;
                    envelope.tags['ai.cloud.roleInstance'] = companyId; // Useful for filtering
                }
            }
        } catch (error) {
            // Don't let telemetry initialization break the app
            console.warn('Error adding user context to telemetry:', error);
        }

        return true; // Send the telemetry
    });

    // Load and initialize Application Insights
    appInsights.loadAppInsights();
    appInsights.trackPageView();
}

// Helper function to track events with automatic user context
export const trackEvent = (name, properties = {}, measurements = {}) => {
    // Skip tracking if Application Insights is not available (PDF routes)
    if (!appInsights) {
        return;
    }

    try {
        const localStorage = require('./utils/localStorage').default;
        const user = localStorage.getFullInfo();
        const companyId = localStorage.getCompanyId();

        const enrichedProperties = {
            ...properties,
            userId: user?.nameid,
            companyId: companyId,
            userDisplayName: user?.displayname,
            // Add correlation ID for end-to-end tracing
            correlationId: generateCorrelationId()
        };

        appInsights.trackEvent({
            name,
            properties: enrichedProperties,
            measurements
        });
    } catch (error) {
        console.warn('Error tracking event:', error);
    }
};

// Helper function to track API calls with correlation
export const trackApiCall = (apiName, method, url, duration, statusCode, correlationId) => {
    // Skip tracking if Application Insights is not available (PDF routes)
    if (!appInsights) {
        return;
    }

    try {
        const localStorage = require('./utils/localStorage').default;
        const user = localStorage.getFullInfo();
        const companyId = localStorage.getCompanyId();

        appInsights.trackDependency({
            target: new URL(url).hostname,
            name: apiName,
            data: `${method} ${url}`,
            duration: duration,
            success: statusCode >= 200 && statusCode < 400,
            resultCode: statusCode.toString(),
            type: 'HTTP',
            properties: {
                userId: user?.nameid,
                companyId: companyId,
                correlationId: correlationId,
                method: method,
                fullUrl: url
            }
        });
    } catch (error) {
        console.warn('Error tracking API call:', error);
    }
};



// Helper function to track exceptions with user context
export const trackException = (exception, properties = {}) => {
    // Skip tracking if Application Insights is not available (PDF routes)
    if (!appInsights) {
        return;
    }

    try {
        const localStorage = require('./utils/localStorage').default;
        const user = localStorage.getFullInfo();
        const companyId = localStorage.getCompanyId();

        const enrichedProperties = {
            ...properties,
            userId: user?.nameid,
            companyId: companyId,
            userDisplayName: user?.displayname
        };

        appInsights.trackException({
            exception,
            properties: enrichedProperties
        });
    } catch (error) {
        console.warn('Error tracking exception:', error);
    }
};

export { reactPlugin, appInsights, getConnectionString }

// Generate correlation ID for request tracing (exported for use in axios interceptor)
export const generateCorrelationId = () => {
    if (!appInsights) {
        return 'pdf-route-' + Math.random().toString(36).substring(2, 11);
    }
    return 'ui-' + Math.random().toString(36).substring(2, 11) + '-' + Date.now().toString(36);
};

// Initialize generateCorrelationId early to avoid hoisting issues
if (typeof window !== 'undefined') {
    window.__generateCorrelationId = generateCorrelationId;
    
    // Add global unhandled promise rejection handler for better debugging
    if (!isPdfRoute && process.env.NODE_ENV === 'development') {
        window.addEventListener('unhandledrejection', event => {
            console.warn('Unhandled promise rejection:', event.reason);
            // Track the rejection in Application Insights if available
            if (appInsights && typeof appInsights.trackException === 'function') {
                appInsights.trackException({
                    exception: new Error(`Unhandled Promise Rejection: ${event.reason}`),
                    properties: {
                        type: 'UnhandledPromiseRejection',
                        reason: String(event.reason),
                        url: window.location.href
                    }
                });
            }
        });
    }
}
